import ConfirmDialog from "components/ConfirmDialog";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTrashablePromises from "hooks/useTrashablePromises";
import React, { useState } from "react";
import { useIntl } from "react-intl";
import { Skus } from "services/KnowfullyAdminClient";
import { Mode, SnackbarMessageProps } from "types/common-types";
import {
  SkuFormProps,
  SkuFormValues,
  SkuSidebarSlots,
} from "types/sku-related-types";
import translateError from "utils/translateError";
import SkuForm from "./SkuDetailsForm";

const SkuDetailsUpdateForm = ({
  sku,
  onDirty,
  onClean,
  onSkuUpdated,
  onProductIdChange,
  sidebarSlots,
}: SkuFormProps & { sidebarSlots?: SkuSidebarSlots | null }) => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const { skuService } = useGlobalContext();
  const [confirmOpen, confirmOpenActions] = useBoolean(false);
  const [formValues, setFormValues] = useState<SkuFormValues | null>(null);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const submit = async (values: SkuFormValues) => {
    confirmOpenActions.setFalse();

    var response = await registerPromise(
      skuService.saveSku(new Skus({ id: sku.id, ...values }))
    );
    onSkuUpdated && onSkuUpdated(sku.id);

    setSnackbarMessage(new SnackbarMessageProps(t({ id: "skuSavedMessage" })));
    return response;
  };

  const onSubmit = async (values: SkuFormValues) => {
    if (!values.hasChild && sku.hasChild) {
      confirmOpenActions.setTrue();
      setFormValues(values);
    } else await submit(values);
  };

  const onConfirm = async () => {
    confirmOpenActions.setFalse();
    try {
      await submit(formValues!);
    } catch (err: any) {
      console.log(err);
      return { [FORM_ERROR]: translateError(err, t) };
    } finally {
      setFormValues(null);
    }
  };

  return (
    <>
      <SkuForm
        sku={sku}
        skuId={sku.id}
        onSubmit={onSubmit}
        submitLabel={t({ id: "skuFormUpdateButton" })}
        onDirty={onDirty}
        onClean={onClean}
        mode={Mode.Update}
        onProductIdChange={onProductIdChange}
        sidebarSlots={sidebarSlots}
      />
      <ConfirmDialog
        dialogTitleKey="skuFormHasChildWarningTitle"
        contentKey="skuFormHasChildWarningQuestion"
        open={confirmOpen}
        onCancel={confirmOpenActions.setFalse}
        handleConfirm={onConfirm}
      />
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default SkuDetailsUpdateForm;
